<script setup lang="ts">
import UpsellingTeaserModal from '@/components/UpsellingTeaserModal.vue'
import { computed, onBeforeMount, ref, watch } from 'vue'
import {
  getEKPUrl,
  isProduction,
  isLocalhostURL,
  isTesting,
} from '@/utils/environment'
import { LightFooter } from '@easybell/caramel-vue3'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { selectNotification } from '@/stores/notifications/utils'
import { storage } from '@/utils/storage/storage'
import { useAuthStore } from '@/stores/auth/store'
import { useCoreStore } from '@/stores/core/store'
import { useCtaStore } from '@/stores/cta/store'
import { useDevStore } from '@/stores/dev/store'
import { useFeaturesStore } from '@/stores/features/store'
import { useI18n } from 'vue-i18n'
import { useMaintenanceStore } from '@/stores/maintenance/store'
import { useNetworkStore } from '@/stores/network/store'
import { useNotificationStore } from '@/stores/notifications/store'
import { useTutorialsStore } from '@/stores/tutorials/store'
import { useUiStore } from '@/stores/ui/store'
import Breadcrumb from '@/components/Breadcrumb.vue'
import ChatOverlay from '@/components/mcpChat/ChatOverlay.vue'
import EnableAnalyticsModal from '@/components/EnableAnalyticsModal.vue'
import EnvInfo from '@/components/EnvInfo.vue'
import Footer from '@/components/Base/Footer/Footer.vue'
import GlobalNotifications from '@/components/Base/Notification/GlobalNotifications.vue'
import HelpButton from '@/components/uca/HelpButton.vue'
import HelpTipsPopup from '@/components/uca/HelpTipsPopup.vue'
import HideHelpTipsModal from '@/components/uca/HideHelpTipsModal.vue'
import LayoutContainer from '@/layouts/LayoutContainer.vue'
import LoadingError from '@/components/LoadingError.vue'
import LoadingIcons from '@/components/LoadingIcons.vue'
import MaintenanceModeModal from '@/components/MaintenanceModeModal.vue'
import MissingSubscription from '@/components/Base/MissingSubscription.vue'
import Navbar from '@/components/Base/Header/Navbar.vue'
import SideMenu from '@/components/uca/utils/layout/SideMenu.vue'
import UcaWelcomeModal from '@/components/uca/UcaWelcomeModal.vue'

const i18n = useI18n()
const coreStore = useCoreStore()
const authStore = useAuthStore()
const featuresStore = useFeaturesStore()
const devStore = useDevStore()
const networkStore = useNetworkStore()
const ctaStore = useCtaStore()
const route = useRoute()
const router = useRouter()
const uiStore = useUiStore()
const notificationStore = useNotificationStore()
const maintenanceStore = useMaintenanceStore()
const isHelpHiglighted = ref(false)

const isLoginPage = computed(() => route.name === 'login')

const isUcaPage = computed(() => route.fullPath.toLowerCase().includes('/uca/'))

const isRouterReady = ref(false)
const isAppBootstrapLoaded = ref(false)
const maintenanceModalMode = computed(() => {
  const { mode } = maintenanceStore

  return mode === 'maintenance' ||
    mode === 'service-unavailable' ||
    mode === 'resolved'
    ? mode
    : undefined
})

const isCallflowPage = computed(() =>
  route.fullPath.toLowerCase().includes('/callflow')
)

// UCA help menu
const tutorialsStore = useTutorialsStore()
const isUcaWelcomeModalVisible = ref(false)

const UCA_WELCOME_TUTORIAL = 'uca_welcome_1' as const

const hasSeenUcaWelcomeModal = computed(() =>
  Object.values(tutorialsStore.finishedTutorials).some(
    (tutorial) => tutorial.name === UCA_WELCOME_TUTORIAL
  )
)

// Password reset page can be accessed unauthenticated; skip tutorial loading there.
const isUcaPageWithTutorials = computed(
  () => isUcaPage.value && route.name !== 'uca-password'
)
const copyrightText = computed(() => `${i18n.t('global.copyrightText')}`)
const footerActions = computed(() => [
  {
    id: 'terms',
    label: i18n.t('footer.privacy.text'),
    href: i18n.t('footer.privacy.url'),
  },
  {
    id: 'privacy',
    label: i18n.t('footer.imprint.text'),
    href: i18n.t('footer.imprint.url'),
  },
  {
    id: 'cancel-contract',
    label: i18n.t('footer.cancelContract.text'),
    underlined: true,
    onClick: () => {
      window.open(
        `${getEKPUrl()}/subscription-management?cancel=true`,
        '_blank'
      )
    },
  },
])
watch(
  isUcaPageWithTutorials,
  async (val) => {
    if (val) {
      await tutorialsStore.loadTutorials()
      isUcaWelcomeModalVisible.value = !hasSeenUcaWelcomeModal.value
    }
  },
  { immediate: true }
)

function highlightHelp() {
  isHelpHiglighted.value = true

  setTimeout(() => (isHelpHiglighted.value = false), 5_000)
}

async function closeUcaWelcomeModal(dontShowAgain: boolean) {
  isUcaWelcomeModalVisible.value = false

  if (!dontShowAgain) {
    return
  }

  notificationStore.add({
    type: 'info',
    title: computed(() => i18n.t('uca.modal.tutorial.welcome.notification')),
  })

  highlightHelp()

  try {
    await tutorialsStore.finishTutorial(UCA_WELCOME_TUTORIAL)
  } catch (error) {
    // Keep the modal closed for this session if persisting fails.
  }
}

async function loadAppBootstrap() {
  if (isAppBootstrapLoaded.value) {
    return
  }

  isAppBootstrapLoaded.value = true

  coreStore.loadTranslations()
  coreStore.loadNavigationTranslations()
  coreStore.setupNpsSurvey()
  if (!isTesting()) setLanguageFromBrowser()
}

onBeforeMount(async () => {
  await router.isReady()

  await loadAppBootstrap()

  isRouterReady.value = true
})

watch(
  () => maintenanceStore.isRecovered,
  async (isRecovered) => {
    if (isRecovered) {
      if (!authStore.isLoggedIn && storage.easybellDeAuth) {
        await authStore.checkLocalStorageForLogin()
      }

      if (isLoginPage.value && authStore.isLoggedIn) {
        await router.replace('/')

        return
      }

      await loadAppBootstrap()
    }
  }
)

async function closeMaintenanceModal() {
  maintenanceStore.close()
  await loadAppBootstrap()
}

function reloadPage() {
  window.location.reload()
}

const showFooter = computed(() => {
  return (
    !isLoginPage.value &&
    !networkStore.loadingPageData &&
    networkStore.showFooter
  )
})

function setLanguageFromBrowser() {
  if (
    document.cookie.split('; ').some((c) => c.startsWith('easybell-locale='))
  ) {
    return
  }
  const browserLang = navigator.language.slice(0, 2)
  const supportedLanguages = ['en', 'de', 'fr', 'nl']
  const language = supportedLanguages.includes(browserLang) ? browserLang : 'en'
  coreStore.changeLanguage(language as 'en' | 'de' | 'fr' | 'nl' | 'debug')
}

// Analytics

const isAnalyticsLoading = ref(false)

function hideAnalyticsDialog() {
  isEnableAnalyticsConfirmationModalVisible.value = false
  document.body.classList.remove('modal-open')
}

const isEnableAnalyticsConfirmationModalVisible = ref(false)

async function enableAnalytics() {
  hideAnalyticsDialog()

  if (ctaStore.getAnalyticsEnabled && !ctaStore.getAnalyticsConsentGiven) {
    useCtaStore().SET_DPA_CONSENT()
  }

  try {
    isAnalyticsLoading.value = true

    const response = await networkStore.activateAnalytics()

    if (
      response.message === 'Activated.' ||
      response.message === 'Already activated.'
    ) {
      isAnalyticsLoading.value = false

      notificationStore.add({
        type: 'success',
        title: computed(() => i18n.t('settings.analytics.enable.success')),
      })
    }
  } catch (error) {
    isAnalyticsLoading.value = false

    notificationStore.add(selectNotification(error))
  }
}

async function openAnalyticsLink() {
  isAnalyticsLoading.value = true

  try {
    const response = await networkStore.loadAnalyticsLink()

    isAnalyticsLoading.value = false

    if (response.link) {
      window.open(response.link, '_blank')
    } else {
      //TODO: Start activating analytics: CTA-2963
    }
  } catch (error) {
    isAnalyticsLoading.value = false

    notificationStore.add(selectNotification(error))
  }
}
async function onAnalyticsButtonClick() {
  if (isAnalyticsLoading.value || uiStore.isBlockedActions) {
    return
  }

  if (ctaStore.ctaData.edition !== 'pro') {
    isUpsellingTeaserModalVisible.value = true
  } else {
    if (!ctaStore.getAnalyticsEnabled || !ctaStore.getAnalyticsConsentGiven) {
      isEnableAnalyticsConfirmationModalVisible.value = true
      document.body.classList.add('modal-open')
    } else {
      openAnalyticsLink()
    }
  }
}

const isUpsellingTeaserModalVisible = ref(false)
</script>

<template>
  <div
    id="app"
    class="bg-neutral-50"
    :class="{
      'test-environment-background':
        !isProduction() && uiStore.background === 'test-environment',
      'ping-background': !isProduction() && uiStore.background === 'ping',
      uca: isUcaPage,
      legacy: !isUcaPage,
    }"
  >
    <div v-if="!isRouterReady" class="min-h-[96vh] pb-64">
      <LoadingIcons />
    </div>

    <div v-else-if="maintenanceStore.blocksApplication" class="min-h-[96vh]" />

    <!-- UCA layout -->
    <div v-else-if="isUcaPage">
      <div class="flex flex-col md:flex-row">
        <SideMenu
          v-if="!isCallflowPage"
          class="md:min-h-screen max-md:w-full sticky top-0"
          @openAnalytics="onAnalyticsButtonClick()"
        />

        <div class="w-full min-w-0">
          <div class="min-h-screen flex flex-col">
            <div>
              <div class="w-full">
                <Breadcrumb />
              </div>

              <LoadingError v-if="networkStore.loadingError" />

              <transition v-else name="fade" mode="out-in">
                <MissingSubscription
                  v-if="networkStore.notFoundError"
                  data-test="overview-message-no-customer"
                />

                <LoadingIcons v-else-if="networkStore.loadingPageData" />

                <div v-else>
                  <RouterView :key="`rerender-key-${devStore.rerenderKey}`" />

                  <!-- Analytics enable confirmation modal -->
                  <EnableAnalyticsModal
                    v-if="isEnableAnalyticsConfirmationModalVisible"
                    data-test="enable-analytics-confirmation-modal"
                    :dpa-consent-missing="
                      !ctaStore.getAnalyticsConsentGiven &&
                      ctaStore.getAnalyticsEnabled
                    "
                    @primaryButtonClick="enableAnalytics()"
                    @secondaryButtonClick="hideAnalyticsDialog()"
                  />

                  <UpsellingTeaserModal
                    v-if="isUpsellingTeaserModalVisible"
                    @close="isUpsellingTeaserModalVisible = false"
                  />
                </div>
              </transition>
            </div>

            <div class="mt-auto">
              <div v-if="showFooter" class="bg-neutral-100">
                <LightFooter
                  :copyright="copyrightText"
                  :actions="footerActions"
                />
                <UpsellingTeaserModal
                  v-if="isUpsellingTeaserModalVisible"
                  @close="isUpsellingTeaserModalVisible = false"
                />
              </div>

              <EnvInfo v-if="!isProduction() && !isLocalhostURL()" />
            </div>
          </div>
        </div>
      </div>

      <UcaWelcomeModal
        v-if="isUcaWelcomeModalVisible"
        @close="closeUcaWelcomeModal($event)"
      />

      <HelpButton
        :isHelpHiglighted="isHelpHiglighted"
        @openUcaWelcomeModal="isUcaWelcomeModalVisible = true"
      />

      <HelpTipsPopup />

      <HideHelpTipsModal />
    </div>

    <!-- Old layout -->
    <div v-else>
      <Navbar v-if="authStore.isLoggedIn" data-test="header-nav-bar" />

      <LayoutContainer>
        <LoadingError v-if="networkStore.loadingError" />

        <transition v-else name="fade" mode="out-in">
          <MissingSubscription
            v-if="networkStore.notFoundError"
            data-test="overview-message-no-customer"
          />

          <LoadingIcons v-else-if="networkStore.loadingPageData" />

          <div v-else>
            <RouterView :key="`rerender-key-${devStore.rerenderKey}`" />
          </div>
        </transition>
      </LayoutContainer>

      <Footer v-if="showFooter" class="w-full" />

      <EnvInfo v-if="!isProduction() && !isLocalhostURL()" />
    </div>
  </div>

  <GlobalNotifications />

  <MaintenanceModeModal
    v-if="maintenanceModalMode"
    :mode="maintenanceModalMode"
    @close="closeMaintenanceModal"
    @reload="reloadPage"
  />
  <ChatOverlay
    v-if="
      authStore.isLoggedIn &&
      featuresStore.hasMcpEndpointFeature &&
      !isCallflowPage
    "
  />
</template>

<style lang="scss">
.mt-20 {
  margin-top: 5rem;
}

.modal-open {
  overflow: hidden;
}

.test-environment-background {
  background-image: url('/test-environment.svg');
}

.ping-background {
  background-image: url('/ping-chillin.svg');
  background-size: 20%;
  animation: 20s ease-in-out 0s infinite shake;
  background-blend-mode: lighten;
}

@keyframes shake {
  0% {
    background-image: url('/ping-chillin.svg');
    background-position-x: 0;
  }

  93% {
    background-image: url('/ping-chillin.svg');
    background-position-x: 0;
  }

  94% {
    background-image: url('/ping-sleeping.webp');
  }

  96% {
    background-position-x: -5%;
  }

  97% {
    background-position-x: 5%;
  }

  98% {
    background-position-x: -3%;
  }

  99% {
    background-position-x: 2%;
  }

  100% {
    background-image: url('/ping-sleeping.webp');
    background-position-x: 0;
  }
}
</style>
